import React from 'react';
import { ResponsiveTabs } from '../../../Components/Common/DashboardKit';
import HrEmbeddedPage from './HrEmbeddedPage';

/**
 * Employees — find and manage an employee.
 * (Round: HR UI Simplification, Phase 1 — composition only.)
 *
 * One employee area with the five visible controls from the spec:
 * Employees · Add employee · Reactivate · Departments · Roles. This
 * replaces the old split where Add/Manage/Departments/Roles were four
 * "Employee management" workspace tabs while Reactivate sat as an
 * unrelated card on the landing.
 *
 * The five views render the EXACT components the old workspace and
 * routes used, unchanged: UserList (search, filters, edit panel,
 * profile data/image, existing validations and approval behavior),
 * Register (Add employee), EmpReactivate, ManageDepartments,
 * ManageRoles. Exposure is preserved precisely: the old Employee
 * management workspace already showed Departments/Roles to every HR
 * viewer (the admin.* route gates apply to the standalone
 * /hr/departments and /hr/roles URLs, which are untouched); the same
 * components render here the same way, and the backend admin API
 * remains the real enforcement — no authority added or removed.
 * Only the active view is mounted (lazy).
 */
const LazyList       = React.lazy(() => import('../UserList'));
const LazyAdd        = React.lazy(() => import('../../Authentication/Register'));
const LazyReactivate = React.lazy(() => import('../EmpReactivate'));

// Round HR-PA follow-up (owner, 2026-08-17): the Departments and Roles admin
// tabs were dropped from this workspace on request — department data lives in
// Epicor (the /api/department proxy) and is not managed here. The standalone
// admin URLs (/hr/departments, /hr/roles) and their backend endpoints are
// untouched; an old ?view=departments|roles deep link now falls back to the
// Employees list below.
const VIEWS = [
    { key: 'list',        label: 'Employees',    icon: 'ri-team-line',        Comp: LazyList },
    { key: 'add',         label: 'Add employee', icon: 'ri-user-add-line',    Comp: LazyAdd },
    { key: 'reactivate',  label: 'Reactivate',   icon: 'ri-refresh-line',     Comp: LazyReactivate },
];

const HrEmployeesSection = ({ view, onViewChange }) => {
    const active = VIEWS.find((v) => v.key === view) || VIEWS[0];
    const Comp = active.Comp;

    return (
        <div>
            <div className="hr-section-title">
                <div>
                    <h5>Employees</h5>
                    <span className="text-muted">Search, manage, add, and reactivate people.</span>
                </div>
            </div>

            <ResponsiveTabs
                tabs={VIEWS.map((v) => ({ key: v.key, label: v.label, icon: v.icon }))}
                activeKey={active.key}
                onChange={onViewChange}
                variant="pills"
                size="sm"
            />

            <HrEmbeddedPage><Comp /></HrEmbeddedPage>
        </div>
    );
};

export default HrEmployeesSection;
